import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { manualIssueApi } from '../api';
import type {
  BatchCreateManualIssueGrantPayload,
  CreateManualIssueGrantPayload,
  ManualIssueGrantListPageInfo,
  ManualIssueGrant,
  ManualIssueStats,
} from '../types';
import { getDefaultStats, getErrorMessage } from '../utils';

const DEFAULT_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 400;

export function useManualIssue() {
  const mountedRef = useRef(true);

  const [stats, setStats] = useState<ManualIssueStats>(getDefaultStats());
  const [grants, setGrants] = useState<ManualIssueGrant[]>([]);
  const [recentGrants, setRecentGrants] = useState<ManualIssueGrant[]>([]);
  const [pageInfo, setPageInfo] = useState<ManualIssueGrantListPageInfo>({
    page: 1,
    limit: DEFAULT_LIMIT,
    totalCount: 0,
  });

  const [page, setPageState] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [search, setSearchState] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingGrants, setLoadingGrants] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const setPage = useCallback((nextPage: number) => {
    setPageState(nextPage);
  }, []);

  const setSearch = useCallback((value: string) => {
    setSearchState(value);
    setPageState(1);
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [search]);

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const result = await manualIssueApi.getStats();
      if (!mountedRef.current) return;
      setStats(result);
    } catch (err) {
      if (mountedRef.current) {
        setError(new Error(getErrorMessage(err)));
      }
    } finally {
      if (mountedRef.current) setLoadingStats(false);
    }
  }, []);

  const fetchGrants = useCallback(
    async (nextPage: number, keyword: string) => {
      setLoadingGrants(true);
      try {
        const result = await manualIssueApi.listGrants({
          page: nextPage,
          limit,
          q: keyword || undefined,
        });
        if (!mountedRef.current) return;
        if (nextPage === 1) {
          setRecentGrants(result.items.slice(0, 4));
        }
        setGrants(result.items);
        setPageInfo(result.pageInfo);
      } catch (err) {
        if (mountedRef.current) {
          setError(new Error(getErrorMessage(err)));
        }
      } finally {
        if (mountedRef.current) setLoadingGrants(false);
      }
    },
    [limit]
  );

  const refetchGrants = useCallback(
    () => fetchGrants(page, debouncedSearch),
    [fetchGrants, page, debouncedSearch]
  );

  const createGrant = useCallback(async (payload: CreateManualIssueGrantPayload) => {
    setSubmitting(true);
    try {
      return await manualIssueApi.createGrant(payload);
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, []);

  const batchCreateGrant = useCallback(async (payload: BatchCreateManualIssueGrantPayload) => {
    setSubmitting(true);
    try {
      const result = await manualIssueApi.batchCreateGrant(payload);
      return result;
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, []);

  const exportGrants = useCallback(async () => {
    setExporting(true);
    try {
      await manualIssueApi.exportGrants();
    } finally {
      if (mountedRef.current) setExporting(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchStats();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchStats]);

  useEffect(() => {
    fetchGrants(page, debouncedSearch);
  }, [fetchGrants, page, debouncedSearch]);

  const pageCount = Math.max(1, Math.ceil(pageInfo.totalCount / limit));

  return useMemo(
    () => ({
      stats,
      grants,
      recentGrants,
      pageInfo,
      page,
      pageCount,
      limit,
      search,
      loadingStats,
      loadingGrants,
      submitting,
      exporting,
      error,
      setPage,
      setSearch,
      refetchStats: fetchStats,
      refetchGrants,
      createGrant,
      batchCreateGrant,
      exportGrants,
    }),
    [
      batchCreateGrant,
      createGrant,
      error,
      exportGrants,
      exporting,
      fetchStats,
      grants,
      recentGrants,
      limit,
      loadingGrants,
      loadingStats,
      page,
      pageCount,
      pageInfo,
      refetchGrants,
      search,
      setPage,
      setSearch,
      stats,
      submitting,
    ]
  );
}
